// app/paywall.tsx
// Version optimisée pour la conversion - Lancement pré-examen

import { useSubscriptionContext } from '@/contexts/SubscriptionContext';
import { RCPackage } from '@/lib/revenuecat';
import { LinearGradient } from 'expo-linear-gradient';
import { router, Stack } from 'expo-router';
import { CheckCircle, Crown, X } from 'phosphor-react-native';
import { useEffect, useState } from 'react';
import { Linking, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';

const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 600;

const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  purple600: '#7C3AED',
  amber600: '#D97706',
  amber500: '#F59E0B',
  amber100: '#FEF3C7',
  emerald600: '#059669',
  emerald500: '#10B981',
  emerald100: '#D1FAE5',
};

// URL des CGU
const CGU_URL = 'https://sites.google.com/view/ernest-cgu';

type PlanType = 'monthly' | 'yearly';

interface PlanOption {
  id: PlanType;
  name: string;
  price: string;
  period: string;
  subtitle: string;
  savingsBadge?: string;
  rcPackage?: RCPackage;
}

const defaultPlans: PlanOption[] = [
  {
    id: 'yearly',
    name: 'Annuel',
    price: '49,99€',
    period: '/an',
    subtitle: '(soit 4,17€/mois)',
    savingsBadge: '🎉 Économise 70€',
  },
  {
    id: 'monthly',
    name: 'Mensuel',
    price: '9,99€',
    period: '/mois',
    subtitle: 'Annulable à tout moment',
  },
];

// ✅ Avantages simplifiés - coches à gauche, pas d'icônes
const features = [
  { text: 'Fiches, Quiz, Flashcards', highlight: 'illimités' },
  { text: 'Coach IA disponible', highlight: '24h/24' },
  { text: 'Cours exclusifs par', highlight: 'Prof Kylian' },
  { text: '100% conforme au', highlight: 'programme officiel' },
];

export default function PaywallScreen() {
  // ✅ Mensuel par défaut (lancement 3 mois avant examen)
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('monthly');
  const [plans, setPlans] = useState<PlanOption[]>(defaultPlans);
  const [isLoading, setIsLoading] = useState(false);
  
  const { 
    offerings,
    canUseInAppPurchase,
    subscribe,
    restorePurchases,
    subscriptionLoading,
  } = useSubscriptionContext();

  // Mettre à jour les plans avec les prix de RevenueCat
  useEffect(() => {
    if (isWeb) return;
    
    if (offerings) {
      const updatedPlans: PlanOption[] = [];
      
      if (offerings.annual) {
        const monthlyEquivalent = (offerings.annual.product.price / 12).toFixed(2);
        updatedPlans.push({
          id: 'yearly',
          name: 'Annuel',
          price: offerings.annual.product.priceString,
          period: '/an',
          subtitle: `(soit ${monthlyEquivalent}€/mois)`,
          savingsBadge: '🎉 Économise 70€',
          rcPackage: offerings.annual,
        });
      } else {
        updatedPlans.push(defaultPlans[0]);
      }
      
      if (offerings.monthly) {
        updatedPlans.push({
          id: 'monthly',
          name: 'Mensuel',
          price: offerings.monthly.product.priceString,
          period: '/mois',
          subtitle: 'Annulable à tout moment',
          rcPackage: offerings.monthly,
        });
      } else {
        updatedPlans.push(defaultPlans[1]);
      }
      
      setPlans(updatedPlans);
    }
  }, [offerings]);

  const handleSubscribe = async () => {
    setIsLoading(true);
    try {
      const success = await subscribe(selectedPlan);
      if (success) {
        router.replace('/(tabs)');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    setIsLoading(true);
    try {
      const success = await restorePurchases();
      if (success) {
        router.replace('/(tabs)');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    router.back();
  };

  const handleLogin = () => {
    router.push('/login');
  };

  const handleConvinceParents = () => {
    router.push('/convince-parents');
  };

  const handleConditions = () => {
    Linking.openURL(CGU_URL);
  };

  const loading = isLoading || subscriptionLoading;

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      
      <View style={styles.container}>
        <LinearGradient
          colors={[colors.indigo100, colors.slate50]}
          style={styles.backgroundGradient}
        />
        
        <ScrollView 
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
        >
          <View style={styles.innerContainer}>
            {/* Header */}
            <View style={styles.header}>
              <TouchableOpacity 
                style={styles.closeButton} 
                onPress={handleClose}
                activeOpacity={0.7}
              >
                <X size={24} color={colors.slate600} weight="bold" />
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.loginButton}
                onPress={handleLogin}
                activeOpacity={0.7}
              >
                <Text style={styles.loginButtonText}>Déjà abonné ?</Text>
              </TouchableOpacity>
            </View>

            {/* ✅ Titre principal - "illimité" surligné */}
            <View style={styles.titleSection}>
              <View style={styles.mainTitleRow}>
                <Text style={styles.mainTitle}>Révise en </Text>
                <View style={styles.highlightWrapper}>
                  <Text style={styles.highlightedWord}>illimité</Text>
                </View>
                <Text style={styles.mainTitle}> avec</Text>
              </View>
              <Text style={styles.mainTitleBrand}>Ernest Premium</Text>
            </View>

            {/* ✅ Plans en premier - focus sur le prix */}
            <View style={styles.plansSection}>
              {plans.map((plan) => {
                const isSelected = selectedPlan === plan.id;
                const isYearly = plan.id === 'yearly';
                
                return (
                  <View key={plan.id}>
                    {isYearly && plan.savingsBadge && (
                      <View style={styles.savingsBadgeContainer}>
                        <View style={styles.savingsBadge}>
                          <Text style={styles.savingsBadgeText}>{plan.savingsBadge}</Text>
                        </View>
                      </View>
                    )}
                    
                    <TouchableOpacity
                      style={[
                        styles.planCard,
                        isSelected && styles.planCardSelected,
                        isYearly && styles.planCardYearly,
                      ]}
                      onPress={() => setSelectedPlan(plan.id)}
                      activeOpacity={0.8}
                    >
                      <View style={styles.planContent}>
                        <View style={[
                          styles.radioOuter,
                          isSelected && styles.radioOuterSelected,
                        ]}>
                          {isSelected && <View style={styles.radioInner} />}
                        </View>
                        
                        <View style={styles.planInfo}>
                          <Text style={[
                            styles.planName,
                            isSelected && styles.planNameSelected,
                          ]}>
                            {plan.name}
                          </Text>
                          <Text style={styles.planSubtitle}>{plan.subtitle}</Text>
                        </View>
                        
                        <View style={styles.planPriceContainer}>
                          <Text style={[
                            styles.planPrice,
                            isSelected && styles.planPriceSelected,
                          ]}>
                            {plan.price}
                          </Text>
                          <Text style={styles.planPeriod}>{plan.period}</Text>
                        </View>
                      </View>
                    </TouchableOpacity>
                  </View>
                );
              })}
            </View>

            {/* ✅ Features avec coches à gauche - pas d'icônes */}
            <View style={styles.featuresSection}>
              {features.map((feature, index) => (
                <View key={index} style={styles.featureRow}>
                  <CheckCircle size={22} color={colors.emerald500} weight="fill" style={styles.featureCheck} />
                  <Text style={styles.featureText}>
                    {feature.text} <Text style={styles.featureHighlight}>{feature.highlight}</Text>
                  </Text>
                </View>
              ))}
            </View>

            {/* Lien Parents */}
            {!isWeb && (
              <TouchableOpacity 
                style={styles.parentsLink}
                onPress={handleConvinceParents}
                activeOpacity={0.7}
              >
                <Text style={styles.parentsLinkText}>
                  Besoin de convaincre tes parents ? Par ici !
                </Text>
              </TouchableOpacity>
            )}

            {/* CTA Button */}
            <View style={styles.ctaSection}>
              <TouchableOpacity
                style={styles.ctaButton}
                onPress={handleSubscribe}
                disabled={loading}
                activeOpacity={0.9}
              >
                <LinearGradient
                  colors={[colors.indigo600, colors.purple600]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.ctaButtonGradient}
                >
                  {loading ? (
                    <ActivityIndicator size="small" color={colors.white} />
                  ) : (
                    <>
                      <Crown size={22} color={colors.white} weight="fill" />
                      <Text style={styles.ctaButtonText}>
                        Débloquer Ernest Premium
                      </Text>
                    </>
                  )}
                </LinearGradient>
              </TouchableOpacity>

              {/* ✅ Lien restauration */}
              {canUseInAppPurchase && (
                <TouchableOpacity
                  style={styles.restoreButton}
                  onPress={handleRestore}
                  disabled={loading}
                  activeOpacity={0.7}
                >
                  <Text style={styles.restoreButtonText}>Restaurer mes achats</Text>
                </TouchableOpacity>
              )}

              {/* ✅ Trust section avec Conditions */}
              <View style={styles.trustSection}>
                <Text style={styles.trustText}>
                  Annulable à tout moment •{' '}
                  <Text style={styles.conditionsLink} onPress={handleConditions}>
                    Conditions
                  </Text>
                </Text>
              </View>
            </View>

            <View style={{ height: 40 }} />
          </View>
        </ScrollView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  backgroundGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 300,
  },
  scrollContent: {
    paddingBottom: 20,
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  innerContainer: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    alignSelf: 'center',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: isWeb ? 24 : 56,
    paddingHorizontal: 20,
    paddingBottom: 8,
  },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  loginButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    backgroundColor: colors.white,
  },
  loginButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.slate600,
  },

  // ✅ Titre principal
  titleSection: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 24,
  },
  mainTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  mainTitle: {
    fontSize: 22,
    fontWeight: '600',
    color: colors.slate700,
    textAlign: 'center',
  },
  highlightWrapper: {
    backgroundColor: colors.amber100,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
  },
  highlightedWord: {
    fontSize: 22,
    fontWeight: '800',
    color: colors.amber600,
  },
  mainTitleBrand: {
    fontSize: 28,
    fontWeight: '800',
    color: colors.indigo600,
    textAlign: 'center',
    letterSpacing: -0.5,
  },

  // ✅ Plans
  plansSection: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  savingsBadgeContainer: {
    alignItems: 'center',
    marginBottom: -14,
    zIndex: 10,
  },
  savingsBadge: {
    backgroundColor: colors.emerald500,
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 100,
    shadowColor: colors.emerald600,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 3,
  },
  savingsBadgeText: {
    color: colors.white,
    fontSize: 13,
    fontWeight: '700',
  },
  planCard: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 18,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: colors.slate200,
  },
  planCardSelected: {
    borderColor: colors.indigo600,
    backgroundColor: colors.indigo50,
  },
  planCardYearly: {
    paddingTop: 22,
  },
  planContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  radioOuter: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: colors.slate300,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  radioOuterSelected: {
    borderColor: colors.indigo600,
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: colors.indigo600,
  },
  planInfo: {
    flex: 1,
  },
  planName: {
    fontSize: 17,
    fontWeight: '700',
    color: colors.slate700,
    marginBottom: 2,
  },
  planNameSelected: {
    color: colors.indigo700,
  },
  planSubtitle: {
    fontSize: 13,
    color: colors.slate500,
  },
  planPriceContainer: {
    alignItems: 'flex-end',
  },
  planPrice: {
    fontSize: 22,
    fontWeight: '800',
    color: colors.slate700,
  },
  planPriceSelected: {
    color: colors.indigo600,
  },
  planPeriod: {
    fontSize: 13,
    color: colors.slate400,
    marginTop: 2,
  },

  // ✅ Features - coches à gauche
  featuresSection: {
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
  },
  featureCheck: {
    marginRight: 12,
  },
  featureText: {
    fontSize: 15,
    color: colors.slate700,
    flex: 1,
  },
  featureHighlight: {
    fontWeight: '700',
    color: colors.slate900,
  },

  // Parents link
  parentsLink: {
    alignItems: 'center',
    marginBottom: 20,
    paddingVertical: 8,
  },
  parentsLinkText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.indigo600,
    textDecorationLine: 'underline',
  },

  // CTA
  ctaSection: {
    paddingHorizontal: 20,
  },
  ctaButton: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: colors.indigo600,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.35,
    shadowRadius: 12,
    elevation: 8,
  },
  ctaButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: 18,
  },
  ctaButtonText: {
    color: colors.white,
    fontSize: 17,
    fontWeight: '700',
  },

  // ✅ Restore button
  restoreButton: {
    alignItems: 'center',
    marginTop: 16,
    paddingVertical: 12,
  },
  restoreButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.indigo600,
  },

  // ✅ Trust section
  trustSection: {
    alignItems: 'center',
    marginTop: 16,
  },
  trustText: {
    fontSize: 13,
    color: colors.slate500,
    textAlign: 'center',
  },
  conditionsLink: {
    color: colors.indigo600,
    textDecorationLine: 'underline',
  },
});
